import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
};

const OVERALL_STYLES = {
  ready_for_rollout: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle, label: 'Ready for Rollout' },
  blocked: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle, label: 'Blocked' },
  in_progress: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Clock, label: 'In Progress' },
};

export default function ReleaseSignoffPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/release/signoffs');
      setData(res.data.data);
    } catch { toast.error('Failed to load sign-offs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    if (!editingRole || !decision) return;
    setSubmitting(true);
    try {
      await api.post('/release/signoffs', { role_key: editingRole, decision, notes: notes || null });
      toast.success('Sign-off submitted');
      setEditingRole(null);
      setDecision('');
      setNotes('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const overall = OVERALL_STYLES[data.overall_status] || OVERALL_STYLES.in_progress;
  const OverallIcon = overall.icon;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="release-signoff-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="h-6 w-6" /> Release Sign-off</h1>
        <p className="text-muted-foreground mt-1">Track approvals from all required reviewers before rollout</p>
      </div>

      {/* Overall Status */}
      <Card className={`border-l-4 ${overall.bg}`}>
        <CardContent className="py-4 px-5">
          <div className="flex items-center gap-3">
            <OverallIcon className={`h-6 w-6 ${overall.color}`} />
            <div>
              <p className={`font-semibold ${overall.color}`}>{overall.label}</p>
              <p className="text-sm text-muted-foreground">
                {data.all_approved ? 'All required approvals received. System is ready for rollout.' :
                  `${data.pending_count} of ${data.signoffs.length} approval(s) pending`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sign-off Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.signoffs.map(s => (
          <Card key={s.role_key} data-testid={`signoff-card-${s.role_key}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{s.role_label}</CardTitle>
                <Badge className={STATUS_STYLES[s.decision]}>{s.decision}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {s.user_name && <p className="text-sm text-muted-foreground">By: {s.user_name}</p>}
              {s.timestamp && <p className="text-xs text-muted-foreground">{new Date(s.timestamp).toLocaleString()}</p>}
              {s.notes && <p className="text-sm mt-2 bg-muted/50 p-2 rounded">{s.notes}</p>}
              {editingRole === s.role_key ? (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">Decision</Label>
                    <Select value={decision} onValueChange={setDecision}>
                      <SelectTrigger data-testid={`decision-select-${s.role_key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes..." data-testid={`notes-input-${s.role_key}`} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingRole(null)}>Cancel</Button>
                    <Button size="sm" onClick={handleSubmit} disabled={!decision || submitting} data-testid={`submit-signoff-${s.role_key}`}>
                      {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => { setEditingRole(s.role_key); setDecision(s.decision === 'pending' ? '' : s.decision); setNotes(s.notes || ''); }} data-testid={`edit-signoff-${s.role_key}`}>
                  <FileText className="h-3.5 w-3.5 mr-1.5" /> {s.decision === 'pending' ? 'Submit Decision' : 'Update'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
